/**
 * Keyboard shortcuts: which key does what. Pure — the table, reading a key
 * press against a combination, and writing a combination the way this
 * keyboard labels it. `stores/shortcuts.js` acts on them and `ui/Kbd.vue`
 * shows each on the button it presses.
 *
 * A combination is a string: `n`, `/`, `?`, `escape`, `mod+enter`. `mod` is
 * ⌘ on a Mac and Ctrl everywhere else — the one modifier people expect.
 * Single keys only ever fire when nothing is being typed into (`isTyping`),
 * so a shortcut never eats a letter meant for a field.
 */

/**
 * The page's keys, and (group `sheet`) the two that belong to whatever is open.
 * There is no list of them to read: every button a key presses shows its key.
 */
export const SHORTCUTS = [
  { id: 'new', keys: 'n', group: 'ledger' },
  { id: 'search', keys: '/', group: 'ledger' },
  { id: 'nextRow', keys: 'j', group: 'ledger' },
  { id: 'prevRow', keys: 'k', group: 'ledger' },
  { id: 'select', keys: 's', group: 'ledger' },
  { id: 'range', keys: 'r', group: 'view' },
  { id: 'prevMonth', keys: '[', group: 'view' },
  { id: 'nextMonth', keys: ']', group: 'view' },
  { id: 'privacy', keys: 'p', group: 'view' },
  { id: 'theme', keys: 'd', group: 'view' },
  { id: 'import', keys: 'i', group: 'open' },
  { id: 'export', keys: 'e', group: 'open' },
  { id: 'tags', keys: 't', group: 'open' },
  { id: 'categories', keys: 'c', group: 'open' },
  { id: 'achievements', keys: 'a', group: 'open' },
  { id: 'console', keys: 'q', group: 'open' },
  { id: 'settings', keys: ',', group: 'open' },
  { id: 'help', keys: 'h', group: 'open' },
  { id: 'confirm', keys: 'mod+enter', group: 'sheet' },
  { id: 'close', keys: 'escape', group: 'sheet' },
]


/** The combination a shortcut id is bound to. */
export const keysOf = (id) => SHORTCUTS.find((s) => s.id === id)?.keys ?? ''

/** Whether this keyboard is a Mac's, which says ⌘ where others say Ctrl. */
export function isMac(nav = globalThis.navigator) {
  const platform = nav?.userAgentData?.platform ?? nav?.platform ?? ''
  return /mac|iphone|ipad|ipod/i.test(platform)
}

const parse = (combo) => {
  const parts = String(combo).toLowerCase().split('+')
  const key = parts.pop()
  return { key, mod: parts.includes('mod'), shift: parts.includes('shift'), alt: parts.includes('alt') }
}

/**
 * Whether a key press is this combination. Shift is not asked about for a key
 * that is itself a shifted character (`?` is Shift+/ on most layouts, and
 * `event.key` already says `?`). Any modifier the combination does not name
 * means no — Ctrl+N is the browser's, not ours.
 */
export function matches(event, combo, mac = isMac()) {
  const want = parse(combo)
  const key = String(event.key ?? '').toLowerCase()
  if (key !== want.key) return false
  const mod = mac ? event.metaKey : event.ctrlKey
  const other = mac ? event.ctrlKey : event.metaKey
  if (mod !== want.mod || other || event.altKey !== want.alt) return false
  // A letter asks for Shift exactly; a symbol has already been shifted.
  if (/^[a-z]$/.test(want.key) && event.shiftKey !== want.shift) return false
  return true
}

const NAMES = { escape: 'Esc', enter: '↵', arrowup: '↑', arrowdown: '↓', arrowleft: '←', arrowright: '→', ' ': 'Space' }

/** The key caps a combination is drawn as: `['⌘', '↵']`, `['Ctrl', '↵']`, `['N']`. */
export function formatCombo(combo, mac = isMac()) {
  const { key, mod, shift, alt } = parse(combo)
  const caps = []
  if (mod) caps.push(mac ? '⌘' : 'Ctrl')
  if (alt) caps.push(mac ? '⌥' : 'Alt')
  if (shift) caps.push(mac ? '⇧' : 'Shift')
  caps.push(NAMES[key] ?? (key.length === 1 ? key.toUpperCase() : key[0].toUpperCase() + key.slice(1)))
  return caps
}

/** The same for `aria-keyshortcuts`, which wants names: `Control+Enter`, `N`. */
export function ariaCombo(combo, mac = isMac()) {
  const { key, mod, shift, alt } = parse(combo)
  const names = []
  if (mod) names.push(mac ? 'Meta' : 'Control')
  if (alt) names.push('Alt')
  if (shift) names.push('Shift')
  names.push(key === 'escape' ? 'Escape' : key === 'enter' ? 'Enter' : key.length === 1 ? key.toUpperCase() : key)
  return names.join('+')
}

/**
 * Whether a key press is going into something being typed in: a text field, a
 * select, anything editable. Checkboxes, radios, sliders and buttons are not —
 * a letter pressed on them is free to be a shortcut.
 */
export function isTyping(target) {
  if (!target || target.nodeType !== 1) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (tag !== 'INPUT') return false
  return !['checkbox', 'radio', 'range', 'button', 'submit', 'reset', 'color', 'file'].includes(
    String(target.type).toLowerCase(),
  )
}
